import {
    ChangeDetectorRef,
    Directive,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { DataChangeLogComponent } from "~app/common/components/data-change-log/data-change-log.component";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { CanBeDirty } from "~app/common/guards/dirty.guard";
import { ConfirmDialogResponse, KundorderTyp } from "~app/common/models/constants";
import { TypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { TypeaheadService } from "~app/common/services/typeahead/typeahead.service";
import { TypeAhead } from "~app/common/services/typeahead/typeahead.types";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { DeleteKundorderCommand } from "../../kundorder/commands/DeleteKundorderCommand";
import { KundorderService } from "../../kundorder/kundorder.service";
import { IKundorderDetailKundorder, IKundorderDetailKundorderrad } from "../../kundorder/models/kundorder-detail-model";
import { CreateOrderradModalComponent } from "../create-orderrad-modal/create-orderrad-modal.component";

@Directive()

export abstract class OrderDetailsComponent implements OnInit, OnDestroy, CanBeDirty {
    readonly busyService = inject(BusyService);
    readonly typeaheadService = inject(TypeaheadService);
    readonly kundorderService = inject(KundorderService);
    readonly commandService = inject(CommandService);
    readonly cd = inject(ChangeDetectorRef);
    readonly toastService = inject(ToastService);
    readonly translateService = inject(TranslateService);
    readonly raskModalService = inject(RaskModalService);
    readonly modalService = inject(NgbModal);
    readonly authService = inject(AuthenticationService);
    readonly dirtyFormService = inject(DirtyFormService);
    readonly activatedRoute = inject(ActivatedRoute);
    readonly router = inject(Router);

    order: IKundorderDetailKundorder;
    rootForm: UntypedFormGroup;
    hookDeregisterFn: any;
    destroy$ = new Subject<void>();

    abstract kundorderTyp: string;
    abstract copy: () => void;

    ngOnInit(): void {
        this.rootForm = new UntypedFormGroup({});

        this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(p => {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            this.loadKundorder(+p["id"]);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadKundorder(id: number) {
        this.busyService.addWork(this.kundorderService
            .get(id))
            .subscribe(res => {
                this.order = res;
            });
    }

    reloadKundorder(): void {
        this.loadKundorder(this.order.id);
    }

    removeClick = (header, text, nextState) => {
        this.raskModalService.confirm(new ModalSettings(this.translateService.instant(header), this.translateService.instant(text)))
            .subscribe(result => {
                if (result === ConfirmDialogResponse.save) {
                    this.remove(nextState);
                }
            });
    };

    remove = (nextState) => {
        const currentCommands = this.commandService.transactionCommandQueue;
        this.commandService.reset();
        this.commandService.addTransactionCommand(new DeleteKundorderCommand(this.order.id));
        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            // eslint-disable-next-line sonarjs/deprecation
            .subscribe(
                () => { // Success
                    this.router.navigate([nextState]);
                },
                () => { // Error
                    this.commandService.transactionCommandQueue = currentCommands;
                }
            );
    };

    undo = () => {
        this.commandService.reset();
        this.reloadKundorder();
    };

    save = async (): Promise<void> => {
        if (!this.canSave()) {
            return;
        }

        this.onBeforeSave().subscribe(confirmResult => {
            if (confirmResult === ConfirmDialogResponse.save) {
                const commands = this.commandService.transactionCommandQueue;
                this.busyService
                    .addWork(this.commandService.pushCommandQueueTransaction())
                    // eslint-disable-next-line sonarjs/deprecation
                    .subscribe(
                        () => {
                            this.toastService.add("Ändringarna sparades");
                            if (this.order.kundorderrader.find(r => r.id === 0) ||
                                // eslint-disable-next-line sonarjs/no-nested-functions
                                this.order.kundorderrader.find(y => y.alternativaHandelssortiment.find(a => ObjectUtils.isNullOrUndefined(a.id)))) {
                                this.reloadKundorder();
                            }
                        },
                        () => {
                            this.commandService.transactionCommandQueue = commands;
                        });
            }
        });
    };

    canEdit() {
        return this.authService.canEdit();
    }

    canSave() {
        const hasValidationErrors = this.kundorderService.alternativaHandelssortimentErrors$.value?.length > 0;
        return this.commandService.isDirty() && this.rootForm.valid && !hasValidationErrors;
    }

    orderradAdded = (kundorderrad) => {
        this.updatePreload(kundorderrad);
        this.order.kundorderrader.push(kundorderrad);
        this.cd.detectChanges();
    };

    removeKundorderrad(kundorderrad: IKundorderDetailKundorderrad) {
        const index = this.order.kundorderrader.indexOf(kundorderrad);
        this.order.kundorderrader.splice(index, 1);
    }

    addOrderrad = () => {
        const header = this.kundorderTyp === KundorderTyp.Kundorder ? "KUNDORDER_DETAIL_LAGG_TILL" : "TERMINALORDER_DETAIL_LAGG_TILL";
        this.raskModalService.open(this.translateService.instant(header), CreateOrderradModalComponent, "xl", true, {
            order: this.order,
            orderradAdded: this.orderradAdded,
            rootForm: this.rootForm
        });
    };

    viewLog = () => {
        const modalRef = this.modalService.open(DataChangeLogComponent, {
            size: "xl",
            centered: false
        });
        modalRef.componentInstance.table = "Kundorder";
        modalRef.componentInstance.referenceId = this.order.id;
    };

    onBeforeSave(): Observable<string> {
        const numRows = this.leveransraderWithKvantitetToBeDeleted();
        if (numRows === 0) {
            return of(ConfirmDialogResponse.save);
        }

        return this.raskModalService.confirm(new ModalSettings("Bekräfta",
            "Leverans- och försörjningsrader samt dess volymer tas bort i samband med att du sparar den nya tidsperioden"));
    }

    readonly isDirtyFn = (): Promise<boolean> => new Promise(resolve => {
        if ((this.commandService.isDirty()) || this.canSave()) {
            const saveFn = !this.commandService.isDirty() && !this.canSave() ? (async () => {
                await this.save();
                resolve(true);
            }) : null;
            return this.dirtyFormService.dirtyFormModal(async () => {
                await this.commandService.reset();
                resolve(true);
            }, saveFn, (arg) => {
                resolve(false);
            });
        } else {
            resolve(true);
            return undefined;
        }
    });

    private updatePreload(kundorderrad: IKundorderDetailKundorderrad) {
        this.typeaheadService.addToPreload(TypeAhead.MottagningsplatsKundorder, this.toTypeahead(kundorderrad.mottagningsplats));
        this.typeaheadService.addToPreload(TypeAhead.Handelssortiment, this.toTypeahead(kundorderrad.handelssortiment));
    }

    private toTypeahead(item: any) {
        return new TypeAheadResult(item.namn, true, item.id);
    }

    private leveransraderWithKvantitetToBeDeleted(): number {
        let numRows = 0;
        this.order.kundorderrader.forEach(kundorderrad => {
            const fromDate = new Date(kundorderrad.periodFrom);
            const toDate = new Date(kundorderrad.periodTom);
            const count = kundorderrad.leveransrader?.filter(
                x => x.kvantitet > 0 && (x.datum < fromDate || x.datum > toDate)
            ).length;
            numRows += ObjectUtils.isNullOrUndefined(count) ? 0 : count;
        });

        return numRows;
    }
}
